import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  EmptyState,
  Pill,
  TextareaField,
} from "../components/design-system/index.js";
import { Toast, useToast } from "../components/toast.js";
import {
  createConversation,
  deleteConversation,
  getConversation,
  listConversations,
  streamChat,
  type AiConversation,
  type AiMessage,
  type AiStreamEvent,
} from "../lib/api-modules/ai.js";

/**
 * /ai — full-page chat with Hearth's conversational AI.
 *
 * Layout:
 *   - Left rail: conversation list, "New conversation" button, archive
 *   - Main pane: message thread + streaming text + tool-call cards
 *   - Composer: textarea + send button at the bottom
 *   - Permanent disclaimer banner pinned to top
 *
 * Streaming model: a single async iterator from `streamChat()` yields
 * Hearth-neutral SSE events. We dispatch each into local message state
 * (text_delta concatenates into a "streaming" message; tool_use_*
 * events accumulate a tool-call card; done finalizes the message).
 *
 * Both light + dark themes via tokens. Privacy mode is wired to
 * mask the assistant text when displayed (the AI itself doesn't get
 * masked output — privacy_mode is communicated to the LLM via the
 * system prompt and the AI is instructed to mask).
 */

interface PendingToolCall {
  callId: string;
  toolName: string;
  argumentsJson: string;
  result?: unknown;
  error?: string;
  citedAuthority?: string;
}

interface RenderedMessage {
  id: string;
  role: "user" | "assistant" | "tool";
  content: string;
  tool_calls?: ReadonlyArray<PendingToolCall>;
}

export function AiPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [composer, setComposer] = useState("");
  const [streamingMessage, setStreamingMessage] = useState("");
  const [streamingToolCalls, setStreamingToolCalls] = useState<PendingToolCall[]>(
    [],
  );
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamError, setStreamError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const conversationsQuery = useQuery({
    queryKey: ["ai", "conversations"],
    queryFn: listConversations,
  });

  const activeConversationQuery = useQuery({
    queryKey: ["ai", "conversation", activeId],
    queryFn: () => getConversation(activeId as string),
    enabled: !!activeId,
  });

  const newConversationMutation = useMutation({
    mutationFn: () => createConversation(),
    onSuccess: async (resp) => {
      await qc.invalidateQueries({ queryKey: ["ai", "conversations"] });
      setActiveId(resp.conversation.conversation_id);
    },
    onError: (err) =>
      toast.show(`Couldn't start a new conversation: ${err.message}`),
  });

  const deleteConversationMutation = useMutation({
    mutationFn: deleteConversation,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["ai", "conversations"] });
      setActiveId(null);
    },
    onError: (err) =>
      toast.show(`Couldn't delete conversation: ${err.message}`),
  });

  // Auto-scroll on new content.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [
    streamingMessage,
    streamingToolCalls.length,
    activeConversationQuery.data?.messages.length,
  ]);

  // Auto-pick first conversation if none active.
  useEffect(() => {
    if (!activeId && conversationsQuery.data?.conversations[0]) {
      setActiveId(conversationsQuery.data.conversations[0].conversation_id);
    }
  }, [activeId, conversationsQuery.data]);

  // Phase 12 — pre-fill composer from `?prompt=...` deep-link (used by
  // the SuggestedPromptsTile across SPA pages). Strip the param after
  // applying so reload doesn't re-fill.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const prefill = searchParams.get("prompt");
    if (prefill) {
      setComposer(prefill);
      const next = new URLSearchParams(searchParams);
      next.delete("prompt");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSend = async () => {
    if (!activeId) return;
    const message = composer.trim();
    if (!message) return;
    setComposer("");
    setStreamingMessage("");
    setStreamingToolCalls([]);
    setStreamError(null);
    setIsStreaming(true);

    try {
      for await (const ev of streamChat({
        conversation_id: activeId,
        user_message: message,
      })) {
        applyEvent(ev, setStreamingMessage, setStreamingToolCalls, setStreamError);
        if (ev.type === "done") break;
      }
    } catch (err) {
      setStreamError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsStreaming(false);
      // Refetch conversation so canonical messages replace the
      // streaming buffer.
      await qc.invalidateQueries({
        queryKey: ["ai", "conversation", activeId],
      });
      await qc.invalidateQueries({ queryKey: ["ai", "conversations"] });
      await qc.invalidateQueries({ queryKey: ["ai", "usage"] });
      setStreamingMessage("");
      setStreamingToolCalls([]);
    }
  };

  const handleNew = () => {
    newConversationMutation.mutate();
  };

  const renderedMessages: RenderedMessage[] = useMemo(() => {
    return (activeConversationQuery.data?.messages ?? []).map((m) =>
      messageToRendered(m),
    );
  }, [activeConversationQuery.data]);

  return (
    <div className="flex h-[calc(100vh-3.5rem)] md:h-screen flex-col md:flex-row">
      {/* === Left rail: conversations === */}
      <aside className="md:w-72 md:shrink-0 md:border-r border-b md:border-b-0 border-[var(--color-border-default)] bg-[var(--color-surface)]">
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border-default)]">
          <span className="font-medium">Conversations</span>
          <Button
            size="xs"
            onClick={handleNew}
            disabled={newConversationMutation.isPending}
          >
            {newConversationMutation.isPending ? "…" : "New"}
          </Button>
        </div>
        {toast.message && (
          <div className="px-4 pt-3">
            <Toast message={toast.message} />
          </div>
        )}
        <ul className="overflow-y-auto md:max-h-[calc(100vh-7rem)] p-2 space-y-1">
          {(conversationsQuery.data?.conversations ?? []).map((c) => (
            <ConversationRow
              key={c.conversation_id}
              conv={c}
              active={c.conversation_id === activeId}
              onClick={() => setActiveId(c.conversation_id)}
            />
          ))}
          {conversationsQuery.data?.conversations.length === 0 && (
            <li className="px-3 py-2 text-sm text-[var(--color-faint)]">
              No conversations yet — click New.
            </li>
          )}
        </ul>
      </aside>

      {/* === Main pane: messages + composer === */}
      <section className="flex flex-1 min-w-0 flex-col">
        <DisclaimerBanner />
        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
          {!activeId && (
            <EmptyState
              title="Select a conversation"
              description="Or click New on the left to start one. Hearth's AI can answer questions about your tax projections, FIRE numbers, capital gains, and more."
            />
          )}
          {renderedMessages.map((m) => (
            <MessageBubble key={m.id} message={m} />
          ))}
          {streamingMessage.length > 0 && (
            <MessageBubble
              message={{
                id: "streaming",
                role: "assistant",
                content: streamingMessage,
                tool_calls: streamingToolCalls,
              }}
              streaming
            />
          )}
          {streamingToolCalls.length > 0 && streamingMessage.length === 0 && (
            <Card>
              <div className="text-sm text-[var(--color-faint)]">
                Hearth is using tools…
                {streamingToolCalls.map((tc) => (
                  <ToolCallCard key={tc.callId} call={tc} />
                ))}
              </div>
            </Card>
          )}
          {streamError && (
            <Card>
              <div className="text-sm text-[var(--color-danger)]">
                Error: {streamError}
              </div>
            </Card>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* === Composer === */}
        <div className="border-t border-[var(--color-border-default)] p-3 bg-[var(--color-surface)]">
          <div className="flex items-end gap-2">
            <TextareaField
              label=""
              value={composer}
              onChange={(e) => setComposer(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (!isStreaming && activeId) void handleSend();
                }
              }}
              placeholder={
                activeId
                  ? "Ask about your finances. Try: 'What's my projected Q3 tax?'"
                  : "Select or create a conversation first…"
              }
              disabled={!activeId || isStreaming}
              className="flex-1"
            />
            <Button
              variant="primary"
              size="md"
              onClick={() => void handleSend()}
              disabled={!activeId || isStreaming || !composer.trim()}
            >
              {isStreaming ? "Streaming…" : "Send"}
            </Button>
          </div>
          {activeConversationQuery.data?.conversation && (
            <ConversationFooter
              conv={activeConversationQuery.data.conversation}
              onDelete={() =>
                deleteConversationMutation.mutate(
                  activeConversationQuery.data.conversation.conversation_id,
                )
              }
            />
          )}
        </div>
      </section>
    </div>
  );
}

function applyEvent(
  ev: AiStreamEvent,
  setText: React.Dispatch<React.SetStateAction<string>>,
  setTools: React.Dispatch<React.SetStateAction<PendingToolCall[]>>,
  setErr: React.Dispatch<React.SetStateAction<string | null>>,
) {
  switch (ev.type) {
    case "text_delta":
      setText((s) => s + ev.delta);
      break;
    case "tool_use_start":
      setTools((tcs) => [
        ...tcs,
        { callId: ev.callId, toolName: ev.toolName, argumentsJson: "" },
      ]);
      break;
    case "tool_use_input_delta":
      setTools((tcs) =>
        tcs.map((tc) =>
          tc.callId === ev.callId
            ? { ...tc, argumentsJson: tc.argumentsJson + ev.partialJson }
            : tc,
        ),
      );
      break;
    case "tool_use_end":
      setTools((tcs) =>
        tcs.map((tc) =>
          tc.callId === ev.callId
            ? { ...tc, argumentsJson: JSON.stringify(ev.arguments) }
            : tc,
        ),
      );
      break;
    case "tool_result":
      setTools((tcs) =>
        tcs.map((tc) =>
          tc.callId === ev.callId
            ? {
                ...tc,
                ...(ev.result !== undefined ? { result: ev.result } : {}),
                ...(ev.error !== undefined ? { error: ev.error } : {}),
                ...(ev.citedAuthority !== undefined
                  ? { citedAuthority: ev.citedAuthority }
                  : {}),
              }
            : tc,
        ),
      );
      break;
    case "error":
      setErr(ev.message);
      break;
    case "usage":
    case "done":
      // No-op; the React Query refetch in handleSend will refresh state.
      break;
  }
}

function messageToRendered(m: AiMessage): RenderedMessage {
  return {
    id: m.message_id,
    role: m.role,
    content: m.content,
    ...(m.tool_calls && m.tool_calls.length > 0
      ? {
          tool_calls: m.tool_calls.map((tc) => ({
            callId: tc.call_id,
            toolName: tc.tool_name,
            argumentsJson: JSON.stringify(tc.arguments),
          })),
        }
      : {}),
  };
}

function ConversationRow({
  conv,
  active,
  onClick,
}: {
  conv: AiConversation;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={[
          "w-full text-left rounded-md px-3 py-2 text-sm transition-colors",
          active
            ? "bg-[var(--color-accent-soft)] font-medium text-[var(--color-accent)]"
            : "text-[var(--color-text)] hover:bg-[var(--color-elevated)]",
        ].join(" ")}
      >
        <div className="truncate">{conv.title}</div>
        <div className="text-xs text-[var(--color-faint)] flex items-center gap-1.5 mt-1">
          <Pill tone="neutral" className="text-[10px]">
            {providerLabel(conv.provider)} {conv.model.split(/[-:]/)[0]}
          </Pill>
        </div>
      </button>
    </li>
  );
}

function MessageBubble({
  message,
  streaming = false,
}: {
  message: RenderedMessage;
  streaming?: boolean;
}) {
  if (message.role === "tool") return null; // tool result merged into assistant card
  const isUser = message.role === "user";
  return (
    <div
      className={["flex", isUser ? "justify-end" : "justify-start"].join(" ")}
    >
      <div
        className={[
          "max-w-[80%] rounded-lg px-4 py-2",
          isUser
            ? "bg-[var(--color-accent-soft)] text-[var(--color-text)]"
            : "bg-[var(--color-surface)] border border-[var(--color-border-default)]",
        ].join(" ")}
      >
        <div className="whitespace-pre-wrap break-words text-sm">
          {message.content}
          {streaming && (
            <span className="inline-block animate-pulse" aria-hidden>
              ▌
            </span>
          )}
        </div>
        {message.tool_calls && message.tool_calls.length > 0 && (
          <div className="mt-2 space-y-1.5">
            {message.tool_calls.map((tc) => (
              <ToolCallCard key={tc.callId} call={tc} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ToolCallCard({ call }: { call: PendingToolCall }) {
  return (
    <details className="rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-elevated)] p-2">
      <summary className="cursor-pointer text-xs font-medium">
        🔧 {call.toolName}
        {call.error ? (
          <Pill tone="danger" className="ml-2 text-[10px]">
            error
          </Pill>
        ) : call.result !== undefined ? (
          <Pill tone="success" className="ml-2 text-[10px]">
            done
          </Pill>
        ) : (
          <Pill tone="neutral" className="ml-2 text-[10px]">
            running
          </Pill>
        )}
      </summary>
      <div className="mt-2 space-y-1.5 text-xs">
        {call.citedAuthority && (
          <div className="text-[var(--color-faint)]">
            cite: {call.citedAuthority}
          </div>
        )}
        {call.error && (
          <div className="text-[var(--color-danger)]">Error: {call.error}</div>
        )}
        {call.result !== undefined && (
          <pre className="overflow-x-auto rounded bg-[var(--color-page)] p-2 font-mono text-[10px]">
            {JSON.stringify(call.result, null, 2)}
          </pre>
        )}
      </div>
    </details>
  );
}

function DisclaimerBanner() {
  return (
    <div className="bg-[var(--color-warning-soft)] px-4 py-2 text-xs text-[var(--color-text)] border-b border-[var(--color-border-default)]">
      AI may make mistakes — confirm with a CPA / financial advisor before acting on tax or financial decisions. When BYOK is active, queries pass through your own provider account.
    </div>
  );
}

function ConversationFooter({
  conv,
  onDelete,
}: {
  conv: AiConversation;
  onDelete: () => void;
}) {
  return (
    <div className="mt-2 flex items-center justify-between text-xs text-[var(--color-faint)]">
      <div className="flex items-center gap-2">
        <Pill tone="neutral" className="text-[10px]">
          {providerLabel(conv.provider)} • {conv.model}
        </Pill>
        <Pill tone="neutral" className="text-[10px]">
          {conv.billing_mode}
        </Pill>
        <span>
          {conv.total_input_tokens.toLocaleString()} in /{" "}
          {conv.total_output_tokens.toLocaleString()} out
        </span>
      </div>
      <button
        type="button"
        onClick={onDelete}
        className="hover:text-[var(--color-danger)]"
      >
        Archive
      </button>
    </div>
  );
}

function providerLabel(p: AiConversation["provider"]): string {
  if (p === "claude") return "Claude";
  if (p === "openai") return "OpenAI";
  return "Bedrock";
}
